import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppContainer from "@src/common/app_container";
import Alert, { AlertProps } from "@src/common/alert";
import { useGenerateMediaAPI, AspectChoice, ResolutionChoice, PersonChoice } from "@src/api/generate_media_api";
import { useFileAPI, FileUploadRequest } from "@src/api/file_api";
import ShareModal from "./share_modal";

type PendingState = "idle" | "submitting" | "polling" | "done" | "error";

interface GeneratedFile {
    generativeOutputId: string;
    assetType: string;
    fileId: string;
    createdAt: string;
    permalink: string;
    thumbnailImage?: string | null;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const POLL_TIMEOUT_MS = 240_000;

const VideoIndex: React.FC = () => {
    const { generateVideo, getResult } = useGenerateMediaAPI();
    const { uploadFile } = useFileAPI();

    const [prompt, setPrompt] = useState("");
    const [negativePrompt, setNegativePrompt] = useState("");

    const [aspectRatio, setAspectRatio] = useState<AspectChoice>("WIDESCREEN");
    const [videoLength, setVideoLength] = useState<4 | 6 | 8>(8);
    const [resolution, setResolution] = useState<ResolutionChoice>("1080p");
    const [generateAudio, setGenerateAudio] = useState(true);

    const [showAdvanced, setShowAdvanced] = useState(false);
    const [seed, setSeed] = useState<number | undefined>(undefined);
    const [personGeneration, setPersonGeneration] = useState<PersonChoice>("allow_adult");
    const [sampleCount, setSampleCount] = useState<1 | 2>(1);

    const [starterImageId, setStarterImageId] = useState<string | undefined>(undefined);
    const [starterImagePermalink, setStarterImagePermalink] = useState<string | undefined>(undefined);

    const [status, setStatus] = useState<AlertProps | null>(null);
    const [pendingState, setPendingState] = useState<PendingState>("idle");
    const [taskQueueId, setTaskQueueId] = useState<string | null>(null);
    const [outputs, setOutputs] = useState<GeneratedFile[]>([]);

    const [selectedIndex, setSelectedIndex] = useState(0);

    const isPortrait = aspectRatio === "VERTICAL";
    const is1080Allowed = !isPortrait;

    useEffect(() => {
        if (!is1080Allowed && resolution === "1080p") setResolution("720p");
    }, [is1080Allowed, resolution]);

    const dropTargetRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const dragCounter = useRef(0);

    const pollingRef = useRef<number | null>(null);
    const pollStartedAtRef = useRef<number>(0);

    useEffect(() => () => { if (pollingRef.current) window.clearInterval(pollingRef.current); }, []);

    const pickFile = () => fileInputRef.current?.click();

    const handleUpload = useCallback(async (rawFile: File) => {
        const req: FileUploadRequest = { files: [rawFile], is_public: false, object_type: "STARTER_IMAGE" };
        const uploaded = await uploadFile(req, () => {});
        const first = uploaded[0];
        if (!first) throw new Error("Upload failed");
        const id = first.id || first.file_id || "";
        setStarterImageId(id);
        setStarterImagePermalink(first.permalink || "");
        return first;
    }, [uploadFile]);

    const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setStatus({ type: "error", message: "Only images are supported as starter media" });
            return;
        }
        try { await handleUpload(file); } catch (err: any) {
            setStatus({ type: "error", message: String(err?.message || err) });
        }
    };

    useEffect(() => {
        const zone = dropTargetRef.current;
        if (!zone) return;

        const onDragEnter = (e: DragEvent) => { e.preventDefault(); e.stopPropagation(); dragCounter.current += 1; setDragActive(true); };
        const onDragOver  = (e: DragEvent) => { e.preventDefault(); e.stopPropagation(); };
        const onDragLeave = (e: DragEvent) => {
            e.preventDefault(); e.stopPropagation();
            dragCounter.current -= 1;
            if (dragCounter.current <= 0) { setDragActive(false); dragCounter.current = 0; }
        };
        const onDrop = async (e: DragEvent) => {
            e.preventDefault(); e.stopPropagation();
            setDragActive(false); dragCounter.current = 0;
            const file = (e as any).dataTransfer?.files?.[0] as File | undefined;
            if (!file) return;
            if (!file.type.startsWith("image/")) { setStatus({ type: "error", message: "Only images are supported as starter media" }); return; }
            try { await handleUpload(file); } catch (err: any) { setStatus({ type: "error", message: String(err?.message || err) }); }
        };

        zone.addEventListener("dragenter", onDragEnter as any);
        zone.addEventListener("dragover", onDragOver as any);
        zone.addEventListener("dragleave", onDragLeave as any);
        zone.addEventListener("drop", onDrop as any);

        const kill = (e: DragEvent) => { e.preventDefault(); e.stopPropagation(); };
        window.addEventListener("dragover", kill as any);
        window.addEventListener("drop", kill as any);

        return () => {
            zone.removeEventListener("dragenter", onDragEnter as any);
            zone.removeEventListener("dragover", onDragOver as any);
            zone.removeEventListener("dragleave", onDragLeave as any);
            zone.removeEventListener("drop", onDrop as any);
            window.removeEventListener("dragover", kill as any);
            window.removeEventListener("drop", kill as any);
        };
    }, [handleUpload]);

    const submit = async () => {
        setStatus(null);
        setOutputs([]);
        setSelectedIndex(0);
        setTaskQueueId(null);
        setPendingState("submitting");
        try {
            const payload: any = {
                action: "video",
                prompt,
                negative_prompt: negativePrompt || undefined,
                aspect_ratio: aspectRatio,
                image_file_id: starterImageId || undefined,
                seed,
                video_length: videoLength,
                video_resolution: resolution,
                generate_audio: generateAudio,
                person_generation: personGeneration,
                sample_count: sampleCount,
            };
            Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

            const resp = await generateVideo(payload);
            const id = resp?.taskQueueId || resp?.id;
            if (!id) throw new Error("Missing taskQueueId");
            setTaskQueueId(id);
            setPendingState("polling");

            if (pollingRef.current) window.clearInterval(pollingRef.current);
            pollStartedAtRef.current = Date.now();

            pollingRef.current = window.setInterval(async () => {
                try {
                    const result = await getResult(id);

                    const taskStatus = result?.task_status ?? result?.taskStatus;
                    const taskResult = result?.task_result ?? result?.taskResult;

                    if (taskStatus === "failed") {
                        let msg = "Video generation failed.";
                        try {
                            const r = typeof taskResult === "string" ? JSON.parse(taskResult) : taskResult;
                            if (r?.type === "veoPromptBlocked") {
                                msg = r?.details?.message || r?.details?.reason || msg;
                            } else if (r?.message) {
                                msg = r.message;
                            }
                        } catch {}
                        setStatus({ type: "error", message: msg });
                        setPendingState("error");
                        if (pollingRef.current) window.clearInterval(pollingRef.current);
                        return;
                    }

                    const files: GeneratedFile[] = Array.isArray(result?.outputs) ? result.outputs : [];
                    if (files.length > 0) {
                        setOutputs(files);
                        setPendingState("done");
                        if (pollingRef.current) window.clearInterval(pollingRef.current);
                        return;
                    }

                    if (taskStatus === "completed") {
                        setStatus({ type: "error", message: "Generation completed but returned no outputs." });
                        setPendingState("error");
                        if (pollingRef.current) window.clearInterval(pollingRef.current);
                        return;
                    }

                    if (Date.now() - pollStartedAtRef.current > POLL_TIMEOUT_MS) {
                        setStatus({
                            type: "error",
                            message: "No video returned within 3 minutes. It may have been blocked by safety filters or failed internally.",
                        });
                        setPendingState("error");
                        if (pollingRef.current) window.clearInterval(pollingRef.current);
                    }
                } catch (e: any) {
                    setStatus({ type: "error", message: String(e.message || e) });
                }
            }, 2000);
        } catch (e: any) {
            setStatus({ type: "error", message: String(e.message || e) });
            setPendingState("error");
        }
    };

    const sortedOutputs = useMemo(
        () => outputs.slice().sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
        [outputs]
    );

    useEffect(() => {
        if (selectedIndex >= sortedOutputs.length) setSelectedIndex(0);
    }, [sortedOutputs.length, selectedIndex]);

    const hasMultiple = sortedOutputs.length > 1;
    const selectedOutput = hasMultiple ? sortedOutputs[selectedIndex] : sortedOutputs[0];

    const [showPreview, setShowPreview] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);

    const promptRef = useRef<HTMLTextAreaElement | null>(null);
    useEffect(() => {
        const el = promptRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, [prompt]);

    return (
        <AppContainer>
            <div className="relative min-h-[calc(100vh-64px)] bg-[#f7f8fb]">
                {status && (
                    <div className="container mx-auto px-6 pt-5">
                        <Alert {...status} onClose={() => setStatus(null)} />
                    </div>
                )}

                <input type="file" accept="image/*" ref={fileInputRef} onChange={onFileChange} className="hidden" />

                {dragActive && <div className="pointer-events-none fixed inset-0 z-40 bg-black/40" />}

                <div className="container mx-auto px-6 py-5">
                    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6 items-start">
                        <div className="rounded-xl bg-white border shadow-sm min-h-[640px] flex flex-col relative">
                            <button
                                onClick={() => setShareOpen(true)}
                                className="absolute top-3 right-3 z-20 px-3 py-1.5 rounded-lg text-sm bg-blue-400 text-white hover:bg-blue-500 border pointer-events-auto"
                                title="Share"
                            >
                                Share
                            </button>

                            <div className="flex-1 flex items-center justify-center p-6">
                                {!selectedOutput ? (
                                    <div className="text-center">
                                        {pendingState === "polling" || pendingState === "submitting" ? (
                                            <div className="flex flex-col items-center gap-2">
                                                <div className="inline-block h-6 w-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                                <div className="text-lg font-medium text-gray-800">Generating…</div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="text-lg font-medium text-gray-800 mb-2">Submit a text prompt to generate videos.</div>
                                                <div className="text-gray-600">Or upload an image for more actions.</div>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="relative w-full">
                                        <video
                                            key={selectedOutput.generativeOutputId}
                                            src={selectedOutput.permalink}
                                            controls
                                            className="bg-black rounded-xl shadow max-w-full h-auto max-h-[540px] mx-auto"
                                        />
                                        {hasMultiple && (
                                            <div className="mt-3 flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => setSelectedIndex((i) => (i - 1 + sortedOutputs.length) % sortedOutputs.length)}
                                                    className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                                                    aria-label="Previous result"
                                                    title="Previous"
                                                >
                                                    ◀
                                                </button>
                                                <div className="text-xs text-gray-700">
                                                    Result {selectedIndex + 1} of {sortedOutputs.length}
                                                </div>
                                                <button
                                                    onClick={() => setSelectedIndex((i) => (i + 1) % sortedOutputs.length)}
                                                    className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                                                    aria-label="Next result"
                                                    title="Next"
                                                >
                                                    ▶
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="border-t px-4 py-3">
                                <div
                                    ref={dropTargetRef}
                                    className="relative rounded-lg p-2 transition border border-transparent"
                                >
                                    <div className="flex items-center gap-3">
                                        <textarea
                                            ref={promptRef}
                                            className="flex-1 text-sm resize-none outline-none border rounded-lg px-3 py-2 overflow-hidden"
                                            placeholder="Write your prompt…"
                                            rows={1}
                                            value={prompt}
                                            onChange={(e) => setPrompt(e.target.value)}
                                        />
                                        <button
                                            onClick={pickFile}
                                            disabled={pendingState === "submitting" || pendingState === "polling"}
                                            className="h-10 px-3 border rounded-lg flex items-center justify-center text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
                                            title="Upload image"
                                        >
                                            Upload
                                        </button>
                                        <button
                                            onClick={submit}
                                            disabled={!prompt || pendingState === "submitting" || pendingState === "polling"}
                                            className="h-10 px-4 border rounded-lg flex items-center justify-center text-sm bg-black text-white hover:bg-gray-800 disabled:opacity-50"
                                            title="Generate"
                                        >
                                            Generate
                                        </button>
                                    </div>
                                </div>

                                {starterImagePermalink && (
                                    <div className="flex items-center gap-3 mt-3">
                                        <div className="relative">
                                            <img
                                                src={starterImagePermalink}
                                                className="h-14 w-14 object-cover rounded border bg-white cursor-pointer"
                                                alt="starter"
                                                onClick={() => setShowPreview(true)}
                                                title="Click to preview"
                                            />
                                            <button
                                                onClick={() => { setStarterImageId(undefined); setStarterImagePermalink(undefined); }}
                                                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-black text-white text-xs flex items-center justify-center shadow"
                                                aria-label="Remove"
                                                title="Remove"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <aside className="rounded-xl bg-white border shadow-sm max-h-[calc(100vh-120px)] overflow-y-auto p-4">
                            <div className="text-sm font-semibold text-gray-900 mb-4">Settings</div>

                            <label className="block text-[12px] text-gray-600 mb-1">Model</label>
                            <div className="mb-4">
                                <div className="w-full border rounded-md p-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed">Veo 3 Preview</div>
                            </div>

                            <div className="mb-4">
                                <div className="text-[12px] text-gray-600 mb-2 text-center">Aspect ratio</div>
                                <div className="flex items-center justify-center gap-3">
                                    <button
                                        onClick={() => setAspectRatio("WIDESCREEN")}
                                        className={`h-16 w-16 border rounded-lg flex flex-col items-center justify-center text-xs ${
                                            aspectRatio === "WIDESCREEN" ? "bg-blue-50 border-blue-600 text-blue-700" : "bg-white"
                                        }`}
                                        aria-pressed={aspectRatio === "WIDESCREEN"}
                                        title="16:9"
                                    >
                                        <div className="text-base">🖥️</div>
                                        16:9
                                    </button>
                                    <button
                                        onClick={() => setAspectRatio("VERTICAL")}
                                        className={`h-16 w-16 border rounded-lg flex flex-col items-center justify-center text-xs ${
                                            aspectRatio === "VERTICAL" ? "bg-blue-50 border-blue-600 text-blue-700" : "bg-white"
                                        }`}
                                        aria-pressed={aspectRatio === "VERTICAL"}
                                        title="9:16"
                                    >
                                        <div className="text-base">📱</div>
                                        9:16
                                    </button>
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="text-[12px] text-gray-600 mb-2">Number of results</div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="range"
                                        min={1}
                                        max={2}
                                        step={1}
                                        value={sampleCount}
                                        onChange={(e) => setSampleCount(Number(e.target.value) as 1 | 2)}
                                        className="w-36"
                                    />
                                    <input
                                        type="number"
                                        min={1}
                                        max={2}
                                        value={sampleCount}
                                        onChange={(e) => setSampleCount(clamp(Number(e.target.value), 1, 2) as 1 | 2)}
                                        className="w-14 border rounded p-1 text-sm text-right"
                                    />
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="block text-[12px] text-gray-600 mb-1">Video length</label>
                                <select
                                    className="w-full border rounded p-2 text-sm"
                                    value={videoLength}
                                    onChange={(e) => setVideoLength(Number(e.target.value) as 4 | 6 | 8)}
                                >
                                    <option value={4}>4 seconds</option>
                                    <option value={6}>6 seconds</option>
                                    <option value={8}>8 seconds</option>
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="block text-[12px] text-gray-600 mb-1">Output resolution</label>
                                <select
                                    className="w-full border rounded p-2 text-sm"
                                    value={resolution}
                                    onChange={(e) => setResolution(e.target.value as ResolutionChoice)}
                                >
                                    <option value="720p">720p</option>
                                    <option value="1080p" disabled={!is1080Allowed}>1080p</option>
                                </select>
                                {!is1080Allowed && <div className="text-[11px] text-gray-500 mt-1">1080p not available for 9:16</div>}
                            </div>

                            <div className="mb-4">
                                <label className="block text-[12px] text-gray-600 mb-1">Generate audio</label>
                                <button
                                    onClick={() => setGenerateAudio(!generateAudio)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition border ${
                                        generateAudio ? "bg-blue-600 border-black" : "bg-gray-200 border-black"
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition border border-black ${
                                            generateAudio ? "translate-x-6" : "translate-x-1"
                                        }`}
                                    />
                                </button>
                            </div>

                            <div className="mb-2">
                                <button
                                    onClick={() => setShowAdvanced((s) => !s)}
                                    className="w-full text-left text-sm font-medium py-2 border-b"
                                >
                                    Advanced {showAdvanced ? "▲" : "▼"}
                                </button>
                                {showAdvanced && (
                                    <div className="pt-3 space-y-3">
                                        <div>
                                            <label className="block text-[12px] text-gray-600 mb-1">
                                                Seed <span className="text-gray-500 font-normal">(optional — auto if blank)</span>
                                            </label>
                                            <input
                                                type="number"
                                                className="w-full border rounded p-2 text-sm"
                                                placeholder="Auto if blank"
                                                value={seed ?? ""}
                                                onChange={(e) => setSeed(e.target.value === "" ? undefined : Number(e.target.value))}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[12px] text-gray-600 mb-1">Person generation</label>
                                            <select
                                                className="w-full border rounded p-2 text-sm"
                                                value={personGeneration}
                                                onChange={(e) => setPersonGeneration(e.target.value as PersonChoice)}
                                            >
                                                <option value="allow_adult">allow_adult</option>
                                                <option value="dont_allow">dont_allow</option>
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </aside>
                    </div>
                </div>

                <ShareModal
                    isOpen={shareOpen}
                    onClose={() => setShareOpen(false)}
                    outputs={sortedOutputs.map(o => ({
                        id: o.generativeOutputId,
                        assetType: "video",
                        permalink: o.permalink,
                        thumbnailImage: o.thumbnailImage ?? undefined,
                    }))}
                />

            </div>
        </AppContainer>
    );
};

export default VideoIndex;
